import { createRef } from 'react';
import { withTranslation } from 'react-i18next';
import groupBy from 'lodash.groupby';
import Parent from '../../../common/parent';
import { BllinkLogger } from '../../../common/utils/bllink_loggers';
import PrintPdfIcons from '../../../common/export_documents/PrintPdfIcons';
import { constants } from '../../../common/constants/constants';
import './balanceReport.scss';
import Select from 'react-select';
import { newSelectWithBorder } from '../../../common/shared/select.styled';
import BllinkUtils from '../../../common/utils/utils';
import {Currency} from "../../../common/currency";

const StringUtils = BllinkUtils.Strings;

class BalanceReport extends Parent {
    constructor(props) {
        super(props);
        document.title = props.t('titles.balance_report');
        this.printSectionRef = createRef();
        this.state = {
            monthInt: parseInt(props.match.params.month),
            monthsTexts: this.getSelectMonthForDropdown(props),
            expensesList: [],
            total: {
                types: [],
                expense: {},
                income: {}
            },
            titleData: {},
            dropdownValue: {}
        }
    }

    async componentDidMount() {
        this.startLoader();
        await this.verifyLoggedIn();
        await this.getBuildingData();
        let year = this.props.match.params.year;
        let month = this.props.match.params.month;
        const response = await this.loadExpensesReportByMonth(month, year);
        const currentDate = this.state.monthsTexts.find(date => date.value === `${month}_${year}`)
        this.setState({
            expensesList: response.expensesList,
            total: response.total,
            dropdownValue: currentDate
        });
        this.makeFullScreen();
        this.endLoader();
    }

    toggleVendorsList = (expense) => {
        const index = this.state.expensesList.findIndex(item => item.value === expense.value);
        this.setState((prevState, props) => ({
            expensesList: [
                ...prevState.expensesList.slice(0, index),
                {
                    ...prevState.expensesList[index],
                    expanded: !expense.expanded
                },
                ...prevState.expensesList.slice(index + 1)
            ]
        }));
    }

    loadExpensesReportByMonth = async (month, year) => {
        let buildingID = this.props.match.params.buildingID;
        let response = await this.client.managers.reports.getBalance(buildingID, year, month) ?? { Expenses: [], Total: 0 };

        BllinkLogger.info(`response manager monthly expenses`, response);
        return {
            expensesList: await this.groupListByKey(response.Expenses, constants.expenses.expenseTypeIdKey),
            total: response.Total
        };
    }

    onMonthAndYearChanged = async monthYearData => {
        this.startLoader();
        let parts = monthYearData.value.split('_');
        let newMonth = parts[0];
        let newYear = parts[1];
        BllinkLogger.info(`newMonthAndYearSelected ${monthYearData} newYear ${newYear} ${newMonth}`);
        const response = await this.loadExpensesReportByMonth(newMonth, newYear);
        this.setState({
            expensesList: response.expensesList,
            total: response.total,
            monthInt: newMonth,
            dropdownValue: monthYearData
        });
        const newUrl = `/${constants.pages.balanceReportUrl}/${this.props.match.params.buildingID}/${newMonth}/${newYear}`
        this.props.history.push({ pathname:newUrl });
        this.endLoader();
    }

    groupListByKey = async (list, key) => {
        const groups = Object.entries(groupBy(list, key));
        const expensesTypes = await this.allExpensesTypes();
        return groups.map(group => {
            return {
                ...expensesTypes.find(item => item.value === parseInt(group[0])),
                vendors: group[1],
                expanded: false
            }
        });
    }

    getDiff = () => {
        let diff = this.Money.roundNumber(this.state.total.income.value - this.state.total.expense.value);
        return this.StringUtils.numberWithCommas(diff);
    }

    getBuildingData = async () => {
        let buildingID = this.props.match.params.buildingID;
        let building = await this.fetchBuildingData(buildingID);
        const titleData = this.buildingTitle(building?.address, undefined, 'expensesBalance');
        BllinkLogger.info(`building title `, titleData);
        this.setState({
            titleData
        })
    }

    renderExpenses = () => {
        const { t } = this.props;
        return (
            <div className={'balance-expenses'}>
                {this.state.expensesList.length > 0 && this.state.expensesList.map(group => {
                    return (
                        <div key={group.value} className={'balance-card'}>
                            <h3 className={'balance-card-title'}>
                                {group.label}
                            </h3>
                            <div className={'balance-card-data'}>
                                <div>
                                    {group.vendors.map((vendor, index) => {
                                        return (
                                            <p
                                                key={vendor.expenseID}
                                                data-testid={vendor.expenseID}
                                                className={`balance-card-row ${index > 2 && !group.expanded ? 'hidden' : ''}`}
                                            >
                                                <span>{vendor.vendorName}</span>

                                                <span className={`value ${StringUtils.checkReverseCurrencyOrder(t(`currency.${vendor.currency}`), t) ? 'currency_header' : ''}`}>
                                                    <span>{vendor.amount}</span>
                                                    <Currency>{vendor.currency}</Currency>
                                                </span>
                                            </p>
                                        );
                                    })}
                                </div>
                                {
                                    group.vendors.length > 3 && (
                                        <a
                                            className={'show-all'}
                                            onClick={() => this.toggleVendorsList(group)}
                                            aria-controls="example-collapse-text"
                                            aria-expanded={group.expanded}
                                        >
                                            {group.expanded ? t('reports.hide') : t('reports.show_all')}
                                        </a>
                                    )
                                }
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    render() {
        let buildingID = this.props.match.params.buildingID;
        let year = this.props.match.params.year;
        const { t } = this.props;
        const { total, titleData } = this.state;
        let currency = total && total.income && total.income.currency ? t(`currency.${total.income.currency}`) : '';

        return (
            <section className={'balance'}>
                {
                    titleData.welcomeKey && (
                        <h1 className={'page-title text-only'}>
                            <span>{`${t('reports.' + titleData.welcomeKey)} `}</span>
                            <span className={'building-title'}>
                                {t('payments.' + titleData.translationKey, titleData)}
                            </span>
                        </h1>
                    )
                }
                <section ref={this.printSectionRef} id="section-to-print">
                    <div className={'balance-topbar'}>
                        <div className={'balance-topbar-select'}>
                            <Select
                                id="select-year"
                                styles={newSelectWithBorder}
                                onChange={this.onMonthAndYearChanged}
                                options={this.state.monthsTexts}
                                value={this.state.dropdownValue}
                            />
                        </div>
                        <div className={'export-icons'}>
                            <PrintPdfIcons
                                pdfContainer={this.printSectionRef.current}
                                buildingID={buildingID}
                                eventLabel="balanceReport"
                                reportName={titleData.welcomeKey ? t('reports.' + titleData.welcomeKey) : t('titles.balance_report')}
                                titleString={t('reports.balance_title')}
                            />
                        </div>
                    </div>

                    {
                        total.types?.length > 0 && (
                            <div className={'balance-report-section'}>
                                <div className={'balance-card'}>
                                    <h3 className={'balance-card-title'}>
                                        {t('reports.balance_income')}
                                    </h3>
                                    <div className={'balance-card-data'}>
                                        {
                                            total.types.map(item => {
                                                return (
                                                    <p key={item.id}>
                                                        <span>{t(`buildings.${item.type}`)}</span>
                                                        <span className={`value ${StringUtils.checkReverseCurrencyOrder(currency, t) ? 'currency_header' : ''}`}>
                                                            <span>{this.StringUtils.numberWithCommas(item.amount)}</span>
                                                            <Currency>{total?.income?.currency}</Currency>
                                                        </span>
                                                    </p>
                                                );
                                            })
                                        }
                                    </div>
                                </div>

                                <h3 className={'text-center balance-subtitle'}>
                                    <span> {t('reports.balance_income')} </span>
                                    <span className={`value_header ${StringUtils.checkReverseCurrencyOrder(currency, t) ? 'currency_header' : ''}`}>
                                        <span>{this.StringUtils.numberWithCommas(total.income.value)}</span>
                                        {currency}
                                    </span>
                                </h3>

                                { this.renderExpenses() }

                                <h3 className={'balance-subtitle'}>
                                    <span> {t('reports.balance_expense')} </span>
                                    <span className={`value_header ${StringUtils.checkReverseCurrencyOrder(currency, t) ? 'currency_header' : ''}`}>
                                        <span>{this.StringUtils.numberWithCommas(total.expense.value)}</span>
                                        {currency}
                                    </span>
                                </h3>

                                <h3 className={'text-center balance-subtitle'}>
                                    {t('reports.balance_monthly_total')}
                                </h3>

                                <div className="balance-table">
                                    <table>
                                        <thead>
                                        <tr>
                                            <th>
                                                <h3>{t('reports.balance_money_in')}</h3>
                                            </th>
                                            <th>
                                                <h3>{t('reports.balance_money_out')}</h3>
                                            </th>
                                            <th>
                                                <h3>{t('reports.balance_diff')}</h3>
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr>
                                            <td>
                                                <span className={`${StringUtils.checkReverseCurrencyOrder(currency, t) ? 'currency_header' : ''}`}>
                                                    <span>{this.StringUtils.numberWithCommas(total.income.value)}</span>
                                                    {currency}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`${StringUtils.checkReverseCurrencyOrder(currency, t) ? 'currency_header' : ''}`}>
                                                    <span>{this.StringUtils.numberWithCommas(total.expense.value)}</span>
                                                    {currency}
                                                </span>
                                            </td>
                                            <td className='diff'>
                                                <span className={`${StringUtils.checkReverseCurrencyOrder(currency, t) ? 'currency_header' : ''}`}>
                                                    <span>{this.getDiff()}</span>
                                                    {currency}
                                                </span>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )
                    }
                </section>
            </section>
        );
    }
}

export default withTranslation()(BalanceReport);
